// One list of everything a person can use: connected accounts (Pipedream, Nango and customer-owned
// apps), their own MCP servers and databases. Each source keeps its own data and actions; this file
// only projects them into rows the Your connections list and the connection panel share.
import type { ScopedConnectionItem } from '../connection-api';
import type { ConnectionReadItem } from '../connection-read-access/api';
import type { CustomMcpConnection } from '../custom-mcp/api';

export type ConnectionRowKind = 'account' | 'server' | 'database';
/** One word per state, everywhere: rows, catalogue tiles and the panel. */
export type ConnectionRowState = 'connected' | 'attention' | 'review' | 'unavailable' | 'off';
export type ConnectionRowAudience = 'private' | 'company' | 'people';
export type ConnectionFilter = 'all' | 'mine' | 'shared' | 'attention';

export interface ConnectionRow {
  key: string;
  kind: ConnectionRowKind;
  name: string;
  /** The account, store, server address or tables: whatever tells two rows of one app apart. */
  identity: string | null;
  /** The id the logo lookup uses (`pipedream:gmail`, `shopify`, `meta-ads`, `postgresql`). */
  logoId: string;
  /** The provider app slug, when the logo comes from the catalogue. */
  appSlug: string | null;
  audience: ConnectionRowAudience;
  /** Unknown when a server's access could not be read: it may well make changes. */
  changes: 'read' | 'write' | 'unknown';
  state: ConnectionRowState;
  /** The connection's own access (who can use it, read or write), when it has one. */
  read: ConnectionReadItem | null;
  item: ScopedConnectionItem | null;
  server: CustomMcpConnection | null;
  connectedAt: string | null;
  lastUsedAt: string | null;
}

const STATE_ORDER: Record<ConnectionRowState, number> = {
  attention: 0,
  review: 1,
  unavailable: 2,
  connected: 3,
  off: 4,
};

export const needsAttention = (state: ConnectionRowState) =>
  state === 'attention' || state === 'review' || state === 'unavailable';

/** An account the person has (or had): not a catalogue entry waiting to be connected. */
export const isConnectedAccount = (item: ScopedConnectionItem) =>
  !['not_connected', 'not_configured'].includes(item.status);

function readState(read: ConnectionReadItem | null | undefined): ConnectionRowState | null {
  if (!read) return null;
  if (['needs_authorization', 'needs_reauthorization'].includes(read.status ?? ''))
    return 'attention';
  if (read.access.status === 'needs_review') return 'review';
  if (!read.access.enabled) return 'off';
  if (read.status === 'unavailable') return 'unavailable';
  return null;
}

function accountState(item: ScopedConnectionItem): ConnectionRowState {
  if (item.status === 'needs_reauthorization') return 'attention';
  if (item.status === 'unavailable') return 'unavailable';
  if (item.status === 'disabled') return 'off';
  if (item.readiness?.state === 'needs_reauthorization') return 'attention';
  if (item.readiness?.state === 'unavailable') return 'unavailable';
  return readState(item.readAccess) ?? 'connected';
}

function audienceOf(
  read: ConnectionReadItem | null | undefined,
  fallback: ConnectionRowAudience,
): ConnectionRowAudience {
  return read?.policy?.audience ?? read?.audience ?? fallback;
}

function changesOf(read: ConnectionReadItem | null | undefined): 'read' | 'write' {
  return read && read.supportsWrites !== false && read.policy?.effectiveChanges === 'allowed'
    ? 'write'
    : 'read';
}

// Only the host: a server address can carry credentials in its path or query.
function hostOf(url: string) {
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

export const appSlugOf = (id: string) =>
  id.startsWith('pipedream:') ? id.slice('pipedream:'.length) : null;

export function accountRow(item: ScopedConnectionItem, key: string): ConnectionRow {
  const read = item.readAccess ?? null;
  return {
    key,
    kind: 'account',
    name: item.name,
    identity: item.accountLabel,
    logoId: item.id,
    appSlug: appSlugOf(item.id),
    audience: audienceOf(read, item.audience),
    changes: changesOf(read),
    state: accountState(item),
    read,
    item,
    server: null,
    connectedAt: item.connectedAt,
    lastUsedAt: item.lastUsedAt,
  };
}

export function serverRow(server: CustomMcpConnection): ConnectionRow {
  const read = server.readAccess ?? null;
  const state: ConnectionRowState =
    server.status === 'needs_authorization'
      ? 'attention'
      : server.status === 'unavailable'
        ? 'unavailable'
        : (readState(read) ?? 'connected');
  return {
    key: `server:${server.id}`,
    kind: 'server',
    name: server.name,
    identity: hostOf(server.url),
    logoId: server.authType === 'shopify_dev_app' ? 'shopify' : 'custom-mcp',
    appSlug: null,
    audience: audienceOf(read, server.audience),
    changes: read ? changesOf(read) : 'unknown',
    state,
    read,
    item: null,
    server,
    connectedAt: null,
    lastUsedAt: null,
  };
}

export function databaseRow(read: ConnectionReadItem): ConnectionRow {
  return {
    key: `database:${read.connectionId}`,
    kind: 'database',
    name: read.name,
    identity: null,
    logoId: 'postgresql',
    appSlug: null,
    audience: audienceOf(read, 'company'),
    changes: changesOf(read),
    state: readState(read) ?? 'connected',
    read,
    item: null,
    server: null,
    connectedAt: null,
    lastUsedAt: null,
  };
}

export function sortRows(rows: ConnectionRow[]) {
  return [...rows].sort(
    (a, b) =>
      STATE_ORDER[a.state] - STATE_ORDER[b.state] ||
      a.name.localeCompare(b.name) ||
      (a.identity ?? '').localeCompare(b.identity ?? ''),
  );
}

export function filterRows(rows: ConnectionRow[], filter: ConnectionFilter) {
  if (filter === 'mine') return rows.filter((row) => row.audience === 'private');
  if (filter === 'shared') return rows.filter((row) => row.audience !== 'private');
  if (filter === 'attention') return rows.filter((row) => needsAttention(row.state));
  return rows;
}

export function filterCounts(rows: ConnectionRow[]): Record<ConnectionFilter, number> {
  return {
    all: rows.length,
    mine: filterRows(rows, 'mine').length,
    shared: filterRows(rows, 'shared').length,
    attention: filterRows(rows, 'attention').length,
  };
}
